import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useSelector, useDispatch, reduxActions } from 'store';
import HydraAIAssistant, { AssistantInfo } from 'components/containers/ChatBase';

import {
  X,
  LayoutSidebarReverse,
  LayoutSplit,
  GripVertical,
  WindowStack,
  Lightbulb,
  FullscreenExit,
  Fullscreen
} from 'react-bootstrap-icons';
import styles from 'theme/components/containers/ChatWindow/ChatWindow.module.scss';

interface ChatWindowProps {
  variant: 'floating' | 'docked';
  onCloseChat: () => void;
  onToggleDock?: () => void;
  onToggleHorizontal?: () => void;
  onToggleResizable?: () => void;
  onToggleFullScreen?: () => void;
  showNavigationBar?: boolean;
  showDragHandle?: boolean;
  isFullScreen?: boolean;
  className?: string;
  searchRef?: React.RefObject<HTMLDivElement>;
  contentWidth?: number; // Maksymalna szerokość dla chat area i input area
}

// Stałe dla ograniczeń rozmiaru okna
const MIN_WINDOW_WIDTH = 300;
const MAX_WINDOW_WIDTH_RATIO = 0.9;
const MIN_WINDOW_HEIGHT = 400;
const MAX_WINDOW_HEIGHT_RATIO = 0.9;
const DEFAULT_FLOATING_WIDTH = 400;

const ChatWindow: React.FC<ChatWindowProps> = ({
  variant,
  onCloseChat,
  onToggleDock,
  onToggleHorizontal,
  onToggleResizable,
  onToggleFullScreen,
  showNavigationBar = true,
  showDragHandle = false,
  isFullScreen = false,
  className,
  searchRef,
  contentWidth
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isChatDocked,
    isChatHorizontal,
    isChatHeaderVisible,
    chatFloatingWidth,
    chatFloatingHeight,
    isChatResizable,
    isChatFullScreen,
    chatThreadLost
  } = useSelector((state) => state.chat);

  // Lista dostępnych asystentów - memoizowane, aby zapobiec niepotrzebnym re-renderom
  const availableAssistants: AssistantInfo[] = useMemo(
    () => [
      {
        name: 'Test WiseB2B',
        symbol: 'Test WiseB2B',
        description: 'Test WiseB2B',
        isDefault: true,
        canAttachFiles: true
      }
    ],
    []
  );

  // Parsowanie parametrów URL - memoizowane, aby zapobiec niepotrzebnym re-renderom
  const urlParameters = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(location.search));
  }, [location.search]);

  const windowRef = useRef<HTMLDivElement>(null);
  const hydraAssistantRef = useRef<{ sendQuickMessage: (message: string) => void }>(null);
  const rafIdRef = useRef<number | null>(null);
  const [resizeState, setResizeState] = useState<{
    isResizing: boolean;
    resizeType: 'width' | 'height' | 'both' | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  }>({
    isResizing: false,
    resizeType: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0
  });

  // Stan lokalny dla płynnego resize'owania bez spamowania Redux
  const [localWidth, setLocalWidth] = useState<number | null>(null);
  const [localHeight, setLocalHeight] = useState<number | null>(null);

  // Zamknij nagłówek chata
  const closeChatHeader = useCallback(() => {
    dispatch(reduxActions.setIsChatHeaderVisible(false));
  }, [dispatch]);

  // Obsługa szybkich akcji (quick actions)
  const handleQuickAction = useCallback((message: string) => {
    if (hydraAssistantRef.current?.sendQuickMessage) {
      hydraAssistantRef.current.sendQuickMessage(message);
    }
  }, []);

  // Handler do zmiany rozmiaru przez uchwyt w prawym dolnym rogu
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (variant !== 'floating' || isFullScreen || !isChatResizable) return;
      e.preventDefault();
      e.stopPropagation();

      setResizeState({
        isResizing: true,
        resizeType: 'both',
        startX: e.clientX,
        startY: e.clientY,
        startWidth: chatFloatingWidth,
        startHeight: chatFloatingHeight
      });
    },
    [variant, isFullScreen, isChatResizable, chatFloatingWidth, chatFloatingHeight]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!resizeState.isResizing || !windowRef.current) return;

      // Anuluj oczekujące RAF, aby zapobiec kolejkowaniu
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Użyj RAF do ograniczenia aktualizacji do 60fps
      rafIdRef.current = requestAnimationFrame(() => {
        const deltaX = e.clientX - resizeState.startX;
        const deltaY = e.clientY - resizeState.startY;

        let newWidth = resizeState.startWidth;
        let newHeight = resizeState.startHeight;

        if (resizeState.resizeType === 'width' || resizeState.resizeType === 'both') {
          newWidth = resizeState.startWidth + deltaX;
        }
        if (resizeState.resizeType === 'height' || resizeState.resizeType === 'both') {
          newHeight = resizeState.startHeight + deltaY;
        }

        // Ograniczenia minimalne i maksymalne rozmiaru okna
        const minWidth = MIN_WINDOW_WIDTH;
        const maxWidth = window.innerWidth * MAX_WINDOW_WIDTH_RATIO;
        const minHeight = MIN_WINDOW_HEIGHT;
        const maxHeight = window.innerHeight * MAX_WINDOW_HEIGHT_RATIO;

        const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

        // Użyj lokalnego stanu podczas resize dla płynnej wydajności
        setLocalWidth(clampedWidth);
        setLocalHeight(clampedHeight);
      });
    },
    [resizeState]
  );

  // Zakończ proces zmiany rozmiaru i zapisz wynik
  const handleMouseUp = useCallback(() => {
    // Anuluj oczekujące RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Zapisz końcowy rozmiar do Redux
    if (localWidth !== null) {
      dispatch(reduxActions.setChatFloatingWidth(localWidth));
    }
    if (localHeight !== null) {
      dispatch(reduxActions.setChatFloatingHeight(localHeight));
    }

    // Wyczyść stan lokalny
    setLocalWidth(null);
    setLocalHeight(null);

    setResizeState({
      isResizing: false,
      resizeType: null,
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0
    });
  }, [localWidth, localHeight, dispatch]);

  // Funkcja pomocnicza do określenia typu kursora na podstawie typu resize
  const getResizeCursor = useCallback((resizeType: 'width' | 'height' | 'both' | null): string => {
    if (resizeType === 'both') return 'nwse-resize';
    if (resizeType === 'width') return 'ew-resize';
    if (resizeType === 'height') return 'ns-resize';
    return 'default';
  }, []);

  // Obsługa globalnych eventów podczas zmiany rozmiaru
  useEffect(() => {
    if (!resizeState.isResizing) return;

    const cursor = getResizeCursor(resizeState.resizeType);
    
    const handleMouseMoveWithCursor = (e: MouseEvent) => {
      // Wymuś odpowiedni kursor w zależności od typu resize
      document.body.style.cursor = cursor;
      handleMouseMove(e);
    };

    // Dodaj globalne listenery
    document.addEventListener('mousemove', handleMouseMoveWithCursor);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp); // Obsługa gdy kursor opuści okno przeglądarki

    // Wymuś kursor i style na całym dokumencie
    document.body.style.cursor = cursor;
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'auto';

    return () => {
      // Usuń listenery
      document.removeEventListener('mousemove', handleMouseMoveWithCursor);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
      
      // Przywróć domyślne style
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';

      // Anuluj oczekujące RAF, aby zapobiec wyciekom pamięci
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [resizeState.isResizing, resizeState.resizeType, handleMouseMove, handleMouseUp, getResizeCursor]);

  // Sprawdź czy powinien być widoczny uchwyt do zmiany rozmiaru
  const showResizeHandle = variant === 'floating' && !isFullScreen && isChatResizable;

  // Stan dla szerokości okna w trybie centralnym
  const [centralWidth, setCentralWidth] = useState<number | null>(null);

  // Oblicza szerokość okna:
  // - W trybie centralnym: szerokość searchRef
  // - W trybie pop-up: szerokość z Redux (chatFloatingWidth)
  const windowWidth = useMemo((): string | undefined => {
    if (variant !== 'floating' || isFullScreen) return undefined;

    // Użyj lokalnego stanu podczas resize dla płynnej wydajności
    if (localWidth !== null && resizeState.isResizing) {
      return `${localWidth}px`;
    }

    if (!isChatResizable) {
      if (centralWidth !== null) {
        return `${centralWidth}px`;
      }
      if (searchRef?.current) {
        const searchRect = searchRef.current.getBoundingClientRect();
        return `${searchRect.width}px`;
      }
    }

    return `${chatFloatingWidth}px`;
  }, [variant, isFullScreen, localWidth, resizeState.isResizing, isChatResizable, centralWidth, searchRef, chatFloatingWidth]);

  // Oblicza wysokość okna
  const windowHeight = useMemo((): string | undefined => {
    if (variant !== 'floating' || isFullScreen) return undefined;

    // Użyj lokalnego stanu podczas resize dla płynnej wydajności
    if (localHeight !== null && resizeState.isResizing) {
      return `${localHeight}px`;
    }

    return `${chatFloatingHeight}px`;
  }, [variant, isFullScreen, localHeight, resizeState.isResizing, chatFloatingHeight]);

  // Aktualizuje szerokość w trybie centralnym, gdy searchRef się zmienia
  useEffect(() => {
    if (variant !== 'floating' || isFullScreen || isChatResizable || !searchRef?.current) {
      return;
    }

    const updateWidth = () => {
      if (searchRef?.current) {
        const searchRect = searchRef.current.getBoundingClientRect();
        setCentralWidth(searchRect.width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    window.addEventListener('scroll', updateWidth, true);

    return () => {
      window.removeEventListener('resize', updateWidth);
      window.removeEventListener('scroll', updateWidth, true);
    };
  }, [variant, isFullScreen, isChatResizable, searchRef]);

  // Działa tylko, jeśli szerokość jest domyślna - nie nadpisuje zmian użytkownika
  useEffect(() => {
    if (
      variant !== 'floating' ||
      isFullScreen ||
      !isChatResizable ||
      !searchRef?.current ||
      chatFloatingWidth !== DEFAULT_FLOATING_WIDTH
    ) {
      return;
    }

    const updateWidth = () => {
      if (searchRef?.current) {
        const searchRect = searchRef.current.getBoundingClientRect();
        dispatch(reduxActions.setChatFloatingWidth(searchRect.width));
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    window.addEventListener('scroll', updateWidth, true);

    return () => {
      window.removeEventListener('resize', updateWidth);
      window.removeEventListener('scroll', updateWidth, true);
    };
  }, [variant, isFullScreen, isChatResizable, searchRef, chatFloatingWidth, dispatch]);

  // Style dla overlay podczas resize'owania
  const resizeOverlayStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      cursor: getResizeCursor(resizeState.resizeType) as any,
      zIndex: 10000,
      pointerEvents: 'auto' as const,
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const
    }),
    [resizeState.resizeType, getResizeCursor]
  );

  // Klasy CSS dla okna chata
  const windowClassName = useMemo(
    () =>
      classnames(styles.chatWindow, className, {
        [styles.chatWindowFloating]: variant === 'floating',
        [styles.chatWindowDocked]: variant === 'docked',
        [styles.chatWindowFullScreen]: isFullScreen
      }),
    [variant, isFullScreen, className]
  );

  // Style dla okna chata
  const windowStyle = useMemo(
    () =>
      variant === 'floating' && !isFullScreen
        ? {
            width: windowWidth,
            height: windowHeight
          }
        : undefined,
    [variant, isFullScreen, windowWidth, windowHeight]
  );

  // Style dla przycisku przeładowania strony
  const reloadButtonStyle = useMemo(
    () => ({
      padding: '0.75rem 1.5rem',
      backgroundColor: '#ffc107',
      color: '#856404',
      border: '1px solid #ffc107',
      borderRadius: '8px',
      cursor: 'pointer' as const,
      fontWeight: 'bold' as const,
      fontSize: '1rem',
      width: '100%',
      maxWidth: '300px',
      margin: '0 auto' as const
    }),
    []
  );

  // Style dla obszaru chata
  const chatContentStyle = useMemo(
    () => ({
      ...(resizeState.isResizing ? { pointerEvents: 'none' as const } : {}),
      ...(contentWidth ? { width: `${contentWidth}px`, margin: '0 auto' as const } : {})
    }),
    [resizeState.isResizing, contentWidth]
  );

  return (
    <>
      {/* Overlay podczas zmiany rozmiaru - przechwytuje eventy myszy */}
      {resizeState.isResizing && (
        <div style={resizeOverlayStyle} onMouseUp={handleMouseUp} />
      )}
      
      <div ref={windowRef} className={windowClassName} style={windowStyle}>
        {showNavigationBar && (
          <div className={styles.navigationBar}>
            {showDragHandle && (
              <div
                className={classnames(styles.dragHandle, 'dragHandle')}
                title={t('Przeciągnij okno')}>
                <GripVertical size={18} />
              </div>
            )}
            <div className={styles.navigationButtons}>
              {
                <button
                  className={classnames(styles.popupButton, {
                    [styles.popupButtonActive]: isChatResizable
                  })}
                  onClick={onToggleResizable}
                  disabled={isChatResizable}
                  aria-label={
                    isChatResizable
                      ? t('Wyłącz tryb regulowanego okienka')
                      : t('Włącz tryb regulowanego okienka')
                  }
                  title={
                    isChatResizable
                      ? t('Wyłącz tryb regulowanego okienka')
                      : t('Włącz tryb regulowanego okienka')
                  }>
                  <WindowStack size={16} />
                </button>
              }
              {onToggleDock && (
                <button
                  className={classnames(styles.dockButton, {
                    [styles.dockedButtonActive]: isChatDocked
                  })}
                  onClick={onToggleDock}
                  disabled={isChatDocked}
                  aria-label={t('Dokuj okno z prawej strony')}
                  title={t('Dokuj okno z prawej strony')}>
                  <LayoutSidebarReverse size={16} />
                </button>
              )}
              {onToggleHorizontal && (
                <button
                  className={classnames(styles.horizontalButton, {
                    [styles.horizontalButtonActive]: isChatHorizontal
                  })}
                  onClick={onToggleHorizontal}
                  disabled={isChatHorizontal}
                  aria-label={
                    isChatHorizontal
                      ? t('Przejdź do trybu floating')
                      : t('Przejdź do trybu poziomego')
                  }
                  title={
                    isChatHorizontal
                      ? t('Przejdź do trybu floating')
                      : t('Przejdź do trybu poziomego')
                  }>
                  <LayoutSplit size={16} />
                </button>
              )}
              {onToggleFullScreen && (
                <button
                  className={classnames(styles.fullScreenButton, {
                    [styles.fullScreenButtonActive]: isChatFullScreen
                  })}
                  disabled={isChatFullScreen}
                  onClick={onToggleFullScreen}
                  title={t('Full screen')}>
                  {isFullScreen ? <FullscreenExit size={16} /> : <Fullscreen size={16} />}
                </button>
              )}
              <button
                className={styles.closeChat}
                onClick={onCloseChat}
                aria-label={t('Zamknij agenta')}
                title={t('Zamknij agenta')}>
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        {!isChatHeaderVisible && (
          <button
            className={styles.chatHeaderToggle}
            onClick={() => dispatch(reduxActions.setIsChatHeaderVisible(true))}
            aria-label={t('Pokaż nagłówek')}
            title={t('Pokaż nagłówek')}>
            <Lightbulb size={14} />
          </button>
        )}
        {isChatHeaderVisible && (
          <div
            className={classnames(styles.chatHeader, {
              [styles.chadHeaderHorizontal]: isChatHorizontal
            })}
            style={{ width: contentWidth ?? 'auto' }}>
            {chatThreadLost ? (
              <div className={styles.welcomeContent}>
                <div className={styles.welcomeHeader}>
                  <h2 className={styles.welcomeTitle}>
                    <span className={styles.welcomeEmoji}>⚠️</span>
                    <span className={styles.welcomeText}>{t('Wątek został utracony')}</span>
                  </h2>
                  <p className={styles.welcomeSubtitle}>
                    {t('Wątek czatu został utracony, prawdopodobnie z powodu restartu serwera. Proszę przeładować stronę, aby kontynuować rozmowę.')}
                  </p>
                </div>
                <div className={styles.welcomeFeatures}>
                  <button
                    onClick={() => window.location.reload()}
                    className={styles.reloadButton}
                    style={reloadButtonStyle}>
                    {t('Przeładuj stronę')}
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.welcomeContent}>
                <div className={styles.welcomeHeader}>
                  <h2 className={styles.welcomeTitle}>
                    <span className={styles.welcomeEmoji}>👋</span>
                    <span className={styles.welcomeText}>{t('Witaj w Agencie AI')}</span>
                  </h2>
                  <p className={styles.welcomeSubtitle}>Twój inteligentny pomocnik w zakupach B2B</p>
                </div>
                <div className={styles.welcomeFeatures}>
                  <div
                    className={`${styles.featureBadge} ${styles.featureBadge1}`}
                    onClick={() => handleQuickAction('O co mogę zapytać?')}
                    style={{ cursor: 'pointer' }}>
                    <span className={styles.featureIcon}>🔍</span>
                    <span className={styles.featureText}>O co mogę zapytać?</span>
                  </div>
                  <div
                    className={`${styles.featureBadge} ${styles.featureBadge2}`}
                    onClick={() => handleQuickAction(t('Pokaż bieżący koszyk'))}
                    style={{ cursor: 'pointer' }}>
                    <span className={styles.featureIcon}>🛒</span>
                    <span className={styles.featureText}>{'Pokaż bieżący koszyk'}</span>
                  </div>
                </div>
              </div>
            )}
            <button
              className={styles.closeChatHeader}
              onClick={closeChatHeader}
              aria-label={t('Ukryj nagłówek')}>
              <X size={18} />
            </button>
          </div>
        )}
        <div className={styles.chatContent} style={chatContentStyle}>
          <HydraAIAssistant
            ref={hydraAssistantRef}
            availableAssistants={availableAssistants}
            urlParameters={urlParameters}
            apiBaseUrl={process.env.REACT_APP_CHAT_API_BASE_URL || 'http://localhost:5427'}
          />
        </div>
        {showResizeHandle && (
          <div
            className={classnames(styles.resizeHandleCorner, 'resizeHandleCorner')}
            title={t('Przeciągnij aby zmienić rozmiar')}
            onMouseDown={handleResizeStart}
          />
        )}
      </div>
    </>
  );
};

export default ChatWindow;
